import * as React from 'react';
import Lazy from 'lazy.js';
import propz from 'propz';
import Moment from 'moment';
import { History } from 'history';
import { AppUser } from '../../../../../App/App';
import { SchoolEvent } from '../../../../../../models/event';
import { SchoolForm } from '../../../../../../models/form';
import { SchoolHouse } from '../../../../../../models/house';
import { SchoolStudentClash } from '../../../../../../models/schoolStudent';
import { Team } from '../../../../../../models/team';
import { Loader } from '../../../../../../components/Loader/Loader';
import { ClashEventsModal } from '../../../../../../components/ClashEventsModal/ClashEventsModal';
import { TwoPanelEditor } from '../../../../../../components/TwoPanelEditor/TwoPanelEditor';
import { Autocomplete } from '../../../../../../components/Autocomplete/Autocomplete';
import { SchoolEventClubEventRadioButtons } from '../SchoolEventClubEventRadioButtons/SchoolEventClubEventRadioButtons';
import {
  ColumnDefinition,
  getPersonListSortedByName,
  isFilterExist2,
  TABLE_SORT_DIRECTION
} from '../../../../../../helpers/table/table';
import { getAge, getEventGeneratedName, getGender } from '../../../../../../helpers/accessor/accessor';
import { getTwoPanelEditorFilters } from '../../../../../../helpers/twoPanelEditor/twoPanelEditor';
import { getFromHistory } from '../../../../../../helpers/history/history';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import { getSchoolEvent } from '../../../../../../helpers/service/admin/event';
import { removeSchoolEventTeam } from '../../../../../../helpers/service/admin/schoolEventTeams';
import { addSchoolEventTeam } from '../../../../../../helpers/service/admin/tournamentTeams';
import { addTeamInClubEvents } from '../../../../../../helpers/service/admin/clubTeams';
import { getSchoolStudentsWithClashesCount } from '../../../../../../helpers/service/admin/schoolTeamPlayers';
import {
  getEventClash,
  getClashEventDisplayTitle,
  getStudentsMethod,
  getPrototypeTeamPlayersMethod,
  isClubEvent,
  isTeamSportEvent,
  isHousesEvent
} from '../../../../../../helpers/event/event';
import { getNameAndAges, searchFunctionSchoolTeams } from '../../../../../../helpers/autocomplete/autocomplete';
import { getUserGenderArrayConvertedFromEventGender } from '../../../../../../helpers/tournament/tournament';
import {
  getSelectOptionForAge,
  getSelectOptionForForms,
  getSelectOptionForGender,
  getSelectOptionForHouses
} from '../../../../../../helpers/table/select';
import { getColorClashes } from '../../../../../../helpers/cell/cell';
import { FILTER_TYPE, LIMIT, DEFAULT_LIMIT } from '../../../../../../consts/table';
import { TEAM_TYPE } from '../../../../../../consts/team';
import { CLUB_EVENT_EDIT_MODE } from '../../../../../../consts/event';
import { AGE_GROUPS } from '../../../../../../consts/school';
import '../SchoolEventManager.scss';
import '../SchoolEventAddExistingTeam/SchoolEventAddExistingTeam.scss';
import BPromise from 'bluebird';

// ─── Column definitions ───────────────────────────────────────────────────────

const ITEMS_ACCUMULATOR_COLUMNS: ColumnDefinition[] = [
  { text: 'Surname', field: 'lastName', accessor: ['lastName'], type: FILTER_TYPE.NONE, isSort: false },
  { text: 'Name', field: 'firstName', accessor: ['firstName'], type: FILTER_TYPE.NONE, isSort: false },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['form', 'name'],
    isMobileHidden: true
  },
  {
    text: 'House',
    field: 'house',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['house', 'name'],
    isMobileHidden: true
  },
  { text: 'Age', field: 'ages', isSort: false, type: FILTER_TYPE.NONE, accessor: getAge, isMobileHidden: true },
  { text: 'Gender', field: 'gender', isSort: false, type: FILTER_TYPE.NONE, accessor: getGender, isMobileHidden: true }
];

const ITEMS_ACCUMULATOR_COLUMNS_WITH_CLASHES: ColumnDefinition[] = [
  { text: 'Clashes', field: 'clashes', cell: getColorClashes, type: FILTER_TYPE.NONE, isSort: false },
  ...ITEMS_ACCUMULATOR_COLUMNS
];

const ITEMS_COLUMNS: ColumnDefinition[] = [
  { text: 'Name', field: 'firstName', accessor: ['firstName'], type: FILTER_TYPE.TEXT, isSort: false },
  { text: 'Surname', field: 'lastName', accessor: ['lastName'], type: FILTER_TYPE.TEXT, isSort: false },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name'],
    isMobileHidden: true
  },
  {
    text: 'House',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name'],
    isMobileHidden: true
  },
  { text: 'Age', field: 'ages', isSort: false, type: FILTER_TYPE.MULTISELECT, accessor: getAge, isMobileHidden: true },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender,
    isMobileHidden: true
  }
];

const ITEMS_COLUMNS_WITH_CLASHES: ColumnDefinition[] = [
  { text: 'Clashes', field: 'clashes', cell: getColorClashes, type: FILTER_TYPE.NONE, isSort: false },
  { text: 'Surname', field: 'lastName', accessor: ['lastName'], type: FILTER_TYPE.TEXT, isSort: false },
  { text: 'Name', field: 'firstName', accessor: ['firstName'], type: FILTER_TYPE.TEXT, isSort: false },
  {
    text: 'Form',
    field: 'forms',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['form', 'name'],
    isMobileHidden: true
  },
  {
    text: 'House',
    field: 'houses',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: ['house', 'name'],
    isMobileHidden: true
  },
  { text: 'Age', field: 'ages', isSort: false, type: FILTER_TYPE.MULTISELECT, accessor: getAge, isMobileHidden: true },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getGender,
    isMobileHidden: true
  }
];

const DEFAULT_TEAM_PLAYERS_ORDER = 'firstName ASC';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Item {
  id: string;
  permissionId: string;
  form: any;
  house: any;
  gender: string;
  firstName: string;
  lastName: string;
  clashes: any;
  playerId?: string;
}

interface Props {
  user: AppUser;
  history: History;
}

interface State {
  event: SchoolEvent;
  forms: SchoolForm[];
  houses: SchoolHouse[];
  isLoading: boolean;
  isItemsLoading: boolean;
  isError: boolean;
  itemsSearch: string;
  nextTeam: Team;

  items: Item[];
  itemsCount: number;
  itemsSelected: Item[];
  itemsFilters: any;
  isShowItemsFilter: boolean;
  isItemsFiltered: boolean;
  isSelectAllItemsChecked: boolean;
  sortItemsDirection: TABLE_SORT_DIRECTION;
  sortItemsColumnsName: string;
  itemCurrentPage: number;

  itemsAccumulator: Item[];
  itemsAccumulatorInitial: Item[];
  itemsAccumulatorCount: number;
  itemsAccumulatorSelected: Item[];
  itemsAccumulatorFilters: any;
  isShowItemsAccumulatorFilter: boolean;
  isItemsAccumulatorFiltered: boolean;
  isSelectAllItemsAccumulatorChecked: boolean;
  sortItemsAccumulatorDirection: TABLE_SORT_DIRECTION;
  sortItemsAccumulatorColumnsName: string;

  itemsAddBuffer: Item[];
  itemsRemoveBuffer: Item[];

  playerEventsModalText: string;
  isPlayerEventsModalOpen: boolean;
  isClubEventsModalOpen: boolean;
  editMode: string;

  itemsHiddenFields: Set<string>;
  itemsAccumulatorHiddenFields: Set<string>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export class SchoolEventExistingTeamManager extends React.Component<Props, State> {
  private itemsSearchRequestId = 0;
  private isSaveInProgress = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      event: undefined,
      forms: [],
      houses: [],
      isLoading: false,
      isItemsLoading: false,
      isError: false,
      itemsSearch: '',
      nextTeam: undefined,

      items: [],
      itemsCount: 0,
      itemsSelected: [],
      itemsFilters: {},
      isShowItemsFilter: false,
      isItemsFiltered: false,
      isSelectAllItemsChecked: false,
      sortItemsDirection: '',
      sortItemsColumnsName: '',
      itemCurrentPage: 1,

      itemsAccumulator: [],
      itemsAccumulatorInitial: [],
      itemsAccumulatorCount: 0,
      itemsAccumulatorSelected: [],
      itemsAccumulatorFilters: {},
      isShowItemsAccumulatorFilter: false,
      isItemsAccumulatorFiltered: false,
      isSelectAllItemsAccumulatorChecked: false,
      sortItemsAccumulatorDirection: '',
      sortItemsAccumulatorColumnsName: '',

      itemsAddBuffer: [],
      itemsRemoveBuffer: [],

      playerEventsModalText: '',
      isPlayerEventsModalOpen: false,
      isClubEventsModalOpen: false,
      editMode: CLUB_EVENT_EDIT_MODE.SINGLE,

      itemsHiddenFields: this.loadHiddenFields('existingTeamManager_items'),
      itemsAccumulatorHiddenFields: this.loadHiddenFields('existingTeamManager_accumulator')
    };
  }

  // ─── localStorage ─────────────────────────────────────────────────────────

  private static ALWAYS_VISIBLE = new Set(['firstName', 'lastName', 'clashes']);

  private get storageUserId(): string {
    return this.props.user?.userId || 'anonymous';
  }

  private loadHiddenFields(key: string): Set<string> {
    const userId = this.props.user?.userId || 'anonymous';
    try {
      const raw = localStorage.getItem(`columnVisibility__${userId}__${key}`);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return new Set<string>(parsed);
    } catch {}
    return new Set();
  }

  private saveHiddenFields(key: string, fields: Set<string>): void {
    try {
      localStorage.setItem(`columnVisibility__${this.storageUserId}__${key}`, JSON.stringify(Array.from(fields)));
    } catch {}
  }

  isColumnToggleable = (field: string) => !SchoolEventExistingTeamManager.ALWAYS_VISIBLE.has(field);

  onToggleItemsColumn = (field: string) => {
    if (SchoolEventExistingTeamManager.ALWAYS_VISIBLE.has(field)) return;
    this.setState(prev => {
      const next = new Set(prev.itemsHiddenFields);
      next.has(field) ? next.delete(field) : next.add(field);
      this.saveHiddenFields('existingTeamManager_items', next);
      return { itemsHiddenFields: next };
    });
  };

  onToggleItemsAccumulatorColumn = (field: string) => {
    if (SchoolEventExistingTeamManager.ALWAYS_VISIBLE.has(field)) return;
    this.setState(prev => {
      const next = new Set(prev.itemsAccumulatorHiddenFields);
      next.has(field) ? next.delete(field) : next.add(field);
      this.saveHiddenFields('existingTeamManager_accumulator', next);
      return { itemsAccumulatorHiddenFields: next };
    });
  };

  // ─── Route ────────────────────────────────────────────────────────────────

  getTeamId = () => getFromHistory(this.props.history, 'team');
  getEventId = () => getFromHistory(this.props.history, 'event');
  getHouseId = () => getFromHistory(this.props.history, 'house');

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  componentDidMount() {
    this.setState({ isLoading: true });
    const { user } = this.props;
    const eventId = this.getEventId();
    const itemsFilters = getTwoPanelEditorFilters(ITEMS_COLUMNS);
    const isShowItemsFilter = isFilterExist2(itemsFilters);

    Promise.all([getSchoolEvent(user, eventId), getAllForms(user), getAllHouses(user)]).then(
      ([event, forms, houses]) => {
        const defaultItemsFilter = this.getDefaultItemsFilter(event);
        const eventName = getEventGeneratedName(event, { user }) || 'Event';
        const tournamentName = propz.get(event, ['tournament', 'name'], '');
        const breadcrumb = tournamentName
          ? `${tournamentName} / Manage events / ${eventName} / Manage participants`
          : `${eventName} / Participants`;
        window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb', { detail: breadcrumb }));
        this.setState({ event, forms, houses, itemsFilters: defaultItemsFilter, isShowItemsFilter, isLoading: false });
      }
    );
  }

  componentWillUnmount() {
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  // ─── Filter helpers ───────────────────────────────────────────────────────

  getDefaultItemsFilter(event: SchoolEvent) {
    const { ages, gender } = event;
    const genderConverted = getUserGenderArrayConvertedFromEventGender(gender);
    const houseId = this.getHouseId();
    return isHousesEvent(event)
      ? { firstName: '', lastName: '', gender: genderConverted, forms: [], ages, houses: [houseId] }
      : { firstName: '', lastName: '', gender: genderConverted, forms: [], ages };
  }

  getTeamItemsFilter(team: Team, event: SchoolEvent) {
    const defaultFilter = this.getDefaultItemsFilter(event);
    const teamGender = getUserGenderArrayConvertedFromEventGender(team.gender);

    return {
      ...defaultFilter,
      ages: team.ages || defaultFilter.ages,
      gender: teamGender.length > 0 ? teamGender : defaultFilter.gender
    };
  }

  getItemsFilter(page: number, accumInitial: Item[], addBuffer: Item[], isAllItems = false) {
    const { itemsFilters, forms, event, itemsSearch } = this.state;
    if (!event) return null;
    const { startTime, endTime, id, players } = event;
    if (!startTime || !endTime) return null;

    const teamId = this.getTeamId();
    const {
      gender: fGender,
      ages: fAges = [],
      firstName: fFirst = '',
      lastName: fLast = '',
      forms: fForms = [],
      houses: fHouses = []
    } = itemsFilters;

    let where: any = { eventId: id, gender: { $in: fGender } };

    const otherTeamPlayerIds = (players || [])
      .filter((p: any) => p.teamId !== teamId)
      .map((p: any) => ({ id: p.userId }));

    const allItems = [...accumInitial, ...addBuffer, ...otherTeamPlayerIds];
    const ninIds = Lazy(allItems)
      .map((x: any) => x.id)
      .uniq()
      .toArray();
    propz.set(where, ['_id', '$nin'], ninIds);

    if (fForms.length > 0) {
      propz.set(where, ['formId', '$in'], fForms);
    } else {
      const ages = fAges.length > 0 ? fAges : AGE_GROUPS.ENGLISH.map((_: any, i: number) => i);
      const formIds = forms.filter(f => ages.some((a: number) => Number(f.age) === Number(a))).map(f => f.id);
      if (formIds.length > 0) propz.set(where, ['formId', '$in'], formIds);
    }

    if (isHousesEvent(event)) propz.set(where, ['houseId'], this.getHouseId());
    if (fHouses.length > 0) propz.set(where, ['houseId', '$in'], fHouses);

    const and: any[] = [];
    if (fFirst.length > 0) and.push({ firstName: { like: fFirst, options: 'i' } });
    if (fLast.length > 0) and.push({ lastName: { like: fLast, options: 'i' } });
    if (itemsSearch.trim().length > 0) {
      const searchValue = itemsSearch.trim();
      and.push({
        $or: [{ firstName: { like: searchValue, options: 'i' } }, { lastName: { like: searchValue, options: 'i' } }]
      });
    }
    if (and.length > 0) propz.set(where, ['$and'], and);

    const s = new Date(startTime);
    const sCopy = new Date(startTime);
    sCopy.setHours(s.getHours() - 1);
    const e = new Date(endTime);
    const eCopy = new Date(endTime);
    eCopy.setHours(e.getHours() + 1);

    propz.set(
      where,
      ['intervalClashStartDate'],
      Moment(sCopy)
        .utc()
        .format()
    );
    propz.set(
      where,
      ['intervalClashEndDate'],
      Moment(eCopy)
        .utc()
        .format()
    );
    propz.set(where, ['fullClashStartDate'], startTime);
    propz.set(where, ['fullClashEndDate'], endTime);

    return {
      where,
      limit: isAllItems ? DEFAULT_LIMIT : LIMIT,
      skip: isAllItems ? 0 : (page - 1) * LIMIT,
      order: DEFAULT_TEAM_PLAYERS_ORDER
    };
  }

  // ─── Normalize ────────────────────────────────────────────────────────────

  normalizePlayers(players: any[]): Item[] {
    return getPersonListSortedByName(players).map(p => ({
      permissionId: p.permissionId,
      form: p.form,
      house: p.house,
      gender: p.gender,
      firstName: p.firstName,
      lastName: p.lastName,
      clashes: p.clashes || { status: 'NONE', fullClashEvents: [], intervalClashEvents: [] },
      id: p.userId || p.id,
      playerId: p.id
    }));
  }

  normalizeStudents(students: SchoolStudentClash[]): Item[] {
    return getPersonListSortedByName(students).map(s => ({
      permissionId: s.permissionId,
      form: s.form,
      house: s.house,
      gender: s.gender,
      firstName: s.firstName,
      lastName: s.lastName,
      clashes: s.clashes,
      id: s.id
    }));
  }

  fetchStudents(page: number, accumInitial: Item[], addBuffer: Item[], isAllItems = false) {
    const { user } = this.props;
    const filter = this.getItemsFilter(page, accumInitial, addBuffer, isAllItems);
    const method = getStudentsMethod(user);
    return Promise.all([method(user, filter), getSchoolStudentsWithClashesCount(user, filter)]);
  }

  // ─── Team selection ───────────────────────────────────────────────────────

  getSchoolTeams = (text: string): Promise<Team[]> => {
    const { user } = this.props;
    const { event } = this.state;
    const { embeddedTeams, ages, gender, sportId } = event;
    const currentTeamId = this.getTeamId();
    const blackList = embeddedTeams
      .filter(t => t.cloneOf && String(t._id) !== String(currentTeamId))
      .map(t => t.cloneOf);
    const houseId = this.getHouseId();
    return searchFunctionSchoolTeams(user, {
      teamIds: blackList,
      text,
      ages,
      gender,
      sportId,
      houses: isHousesEvent(event) ? [{ id: houseId }] : []
    });
  };

  onSelectTeam = (team: Team) => {
    const { user } = this.props;
    const { event } = this.state;
    const itemsFilters = this.getTeamItemsFilter(team, event);
    this.setState({ isLoading: true, itemsFilters });

    const { startTime, endTime } = event;
    const s = new Date(startTime);
    const sCopy = new Date(startTime);
    sCopy.setHours(s.getHours() - 1);
    const e = new Date(endTime);
    const eCopy = new Date(endTime);
    eCopy.setHours(e.getHours() + 1);

    const clashWhere: any = {};
    propz.set(
      clashWhere,
      ['intervalClashStartDate'],
      Moment(sCopy)
        .utc()
        .format()
    );
    propz.set(
      clashWhere,
      ['intervalClashEndDate'],
      Moment(eCopy)
        .utc()
        .format()
    );
    propz.set(clashWhere, ['fullClashStartDate'], startTime);
    propz.set(clashWhere, ['fullClashEndDate'], endTime);

    const accumFilters = getTwoPanelEditorFilters(ITEMS_ACCUMULATOR_COLUMNS);

    getPrototypeTeamPlayersMethod(user)(user, team.id, { where: clashWhere }).then(players => {
      const normalized = this.normalizePlayers(players);
      this.setState({ itemsFilters }, () => {
        this.fetchStudents(1, normalized, []).then(([students, studentsCount]) => {
          this.setState({
            nextTeam: team,
            itemsAccumulator: normalized,
            itemsAccumulatorInitial: normalized,
            itemsAccumulatorCount: normalized.length,
            itemsAccumulatorFilters: accumFilters,
            isShowItemsAccumulatorFilter: isFilterExist2(accumFilters),
            isItemsAccumulatorFiltered: false,
            itemsAddBuffer: [],
            itemsRemoveBuffer: [],
            items: this.normalizeStudents(students),
            itemsCount: studentsCount.count,
            itemCurrentPage: 1,
            itemsSelected: [],
            itemsAccumulatorSelected: [],
            isSelectAllItemsChecked: false,
            isSelectAllItemsAccumulatorChecked: false,
            isLoading: false,
            isError: false
          });
        });
      });
    });
  };

  // ─── Add / Remove ─────────────────────────────────────────────────────────

  onAddClick = () => {
    const { itemsSelected, itemsAddBuffer, itemsAccumulatorInitial, itemsAccumulator, itemCurrentPage } = this.state;
    const nextAddBuffer = [...itemsAddBuffer, ...itemsSelected];
    const existingIds = new Set(itemsAccumulator.map(x => String(x.id)));
    const nextAccumulator = [...itemsAccumulator, ...itemsSelected.filter(x => !existingIds.has(String(x.id)))];
    this.setState({ isLoading: true });
    this.fetchStudents(itemCurrentPage, itemsAccumulatorInitial, nextAddBuffer).then(([students, count]) => {
      this.setState({
        items: this.normalizeStudents(students),
        itemsCount: count.count,
        itemsAddBuffer: nextAddBuffer,
        itemsAccumulator: nextAccumulator,
        itemsAccumulatorCount: nextAccumulator.length,
        itemsSelected: [],
        itemsAccumulatorSelected: [],
        isSelectAllItemsChecked: false,
        isSelectAllItemsAccumulatorChecked: false,
        isLoading: false
      });
    });
  };

  onRemoveClick = () => {
    const {
      itemsAccumulatorSelected,
      itemsAddBuffer,
      itemsRemoveBuffer,
      itemsAccumulatorInitial,
      itemsAccumulator,
      itemCurrentPage
    } = this.state;
    const selectedIds = new Set(itemsAccumulatorSelected.map(x => String(x.id)));
    const addBufferIds = new Set(itemsAddBuffer.map(x => String(x.id)));
    const nextAddBuffer = itemsAddBuffer.filter(x => !selectedIds.has(String(x.id)));
    const existingParticipants = itemsAccumulatorSelected.filter(x => !addBufferIds.has(String(x.id)));
    const existingRemoveIds = new Set(itemsRemoveBuffer.map(x => String(x.id)));
    const nextRemoveBuffer = [
      ...itemsRemoveBuffer,
      ...existingParticipants.filter(x => !existingRemoveIds.has(String(x.id)))
    ];
    const nextAccumInitial = itemsAccumulatorInitial.filter(
      p => !nextRemoveBuffer.some(r => String(r.id) === String(p.id))
    );
    const nextAccumulator = itemsAccumulator.filter(x => !selectedIds.has(String(x.id)));
    this.setState({ isLoading: true });
    this.fetchStudents(itemCurrentPage, nextAccumInitial, nextAddBuffer).then(([students, count]) => {
      this.setState({
        items: this.normalizeStudents(students),
        itemsCount: count.count,
        itemsAddBuffer: nextAddBuffer,
        itemsRemoveBuffer: nextRemoveBuffer,
        itemsAccumulatorInitial: nextAccumInitial,
        itemsAccumulator: nextAccumulator,
        itemsAccumulatorCount: nextAccumulator.length,
        itemsSelected: [],
        itemsAccumulatorSelected: [],
        isSelectAllItemsChecked: false,
        isSelectAllItemsAccumulatorChecked: false,
        isLoading: false
      });
    });
  };

  // ─── Click handlers ───────────────────────────────────────────────────────

  onItemClick = (index: number) => {
    const { items, itemsSelected } = this.state;
    const item = items[index];
    const idx = itemsSelected.findIndex(x => x.id === item.id);
    const next = [...itemsSelected];
    idx !== -1 ? next.splice(idx, 1) : next.push(item);
    this.setState({ itemsSelected: next, itemsAccumulatorSelected: [] });
  };

  onItemAccumulatorClick = (index: number) => {
    const { itemsAccumulator, itemsAccumulatorSelected } = this.state;
    const item = itemsAccumulator[index];
    const idx = itemsAccumulatorSelected.findIndex(x => x.id === item.id);
    const next = [...itemsAccumulatorSelected];
    idx !== -1 ? next.splice(idx, 1) : next.push(item);
    this.setState({ itemsAccumulatorSelected: next, itemsSelected: [] });
  };

  // ─── Select all ───────────────────────────────────────────────────────────

  onSelectAllItemsClick = () => {
    const { itemsAccumulatorInitial, itemsAddBuffer } = this.state;
    this.setState({ isLoading: true });
    this.fetchStudents(1, itemsAccumulatorInitial, itemsAddBuffer, true).then(([students]) => {
      const normalizedStudents = this.normalizeStudents(students);
      const selectedIds = new Set(this.state.itemsSelected.map(item => String(item.id)));
      this.setState({
        itemsSelected: [
          ...this.state.itemsSelected,
          ...normalizedStudents.filter(item => !selectedIds.has(String(item.id)))
        ],
        isSelectAllItemsChecked: true,
        isLoading: false
      });
    });
  };
  onSelectAllItemsOnPageClick = () =>
    this.setState(s => {
      const selectedIds = new Set(s.itemsSelected.map(item => String(item.id)));
      return {
        itemsSelected: [...s.itemsSelected, ...s.items.filter(item => !selectedIds.has(String(item.id)))],
        isSelectAllItemsChecked: true
      };
    });
  onUnselectAllItemsClick = () => this.setState({ itemsSelected: [], isSelectAllItemsChecked: false });
  onSelectAllItemsAccumulatorClick = () =>
    this.setState(s => ({ itemsAccumulatorSelected: s.itemsAccumulator, isSelectAllItemsAccumulatorChecked: true }));
  onSelectAllItemsAccumulatorOnPageClick = () =>
    this.setState(s => ({ itemsAccumulatorSelected: s.itemsAccumulator, isSelectAllItemsAccumulatorChecked: true }));
  onUnselectAllItemsAccumulatorClick = () =>
    this.setState({ itemsAccumulatorSelected: [], isSelectAllItemsAccumulatorChecked: false });

  // ─── Pagination ───────────────────────────────────────────────────────────

  itemsSetCurrentPageParams = (page: number) => {
    const { itemsAccumulatorInitial, itemsAddBuffer } = this.state;
    this.setState({ isLoading: true, itemCurrentPage: page });
    this.fetchStudents(page, itemsAccumulatorInitial, itemsAddBuffer).then(([students, count]) => {
      this.setState({ items: this.normalizeStudents(students), itemsCount: count.count, isLoading: false });
    });
  };

  // ─── Filters – items ──────────────────────────────────────────────────────

  onItemsFilterChange = (event: any, field: string) => {
    const value = event.target ? event.target.value : event;
    this.setState(s => ({ itemsFilters: { ...s.itemsFilters, [field]: value } }));
  };
  onItemsFilterClick = () => this.setState(s => ({ isShowItemsFilter: !s.isShowItemsFilter }));

  itemsOnApplyFilterClick = (isSilent = false) => {
    const { itemsAccumulatorInitial, itemsAddBuffer } = this.state;
    const requestId = ++this.itemsSearchRequestId;
    if (isSilent) {
      this.setState({ isItemsLoading: true, itemCurrentPage: 1 });
    } else {
      this.setState({ isLoading: true, itemCurrentPage: 1 });
    }
    this.fetchStudents(1, itemsAccumulatorInitial, itemsAddBuffer).then(([students, count]) => {
      if (requestId !== this.itemsSearchRequestId) return;
      this.setState({
        items: this.normalizeStudents(students),
        itemsCount: count.count,
        isItemsFiltered: true,
        isLoading: isSilent ? this.state.isLoading : false,
        isItemsLoading: false
      });
    });
  };

  onItemsSearchChange = (itemsSearch: string) => {
    this.setState({ itemsSearch, isItemsLoading: true }, () => this.itemsOnApplyFilterClick(true));
  };

  onReplaceItemsFilters = (itemsFilters: any) => {
    this.setState({ itemsFilters }, this.itemsOnApplyFilterClick);
  };

  itemsOnClearFilterClick = () => {
    const { event, nextTeam, itemsAccumulatorInitial, itemsAddBuffer } = this.state;
    const defaultFilter = nextTeam ? this.getTeamItemsFilter(nextTeam, event) : this.getDefaultItemsFilter(event);
    this.setState({ isLoading: true, itemsFilters: defaultFilter, itemCurrentPage: 1 }, () => {
      this.fetchStudents(1, itemsAccumulatorInitial, itemsAddBuffer).then(([students, count]) => {
        this.setState({
          items: this.normalizeStudents(students),
          itemsCount: count.count,
          isItemsFiltered: false,
          isLoading: false
        });
      });
    });
  };

  // ─── Filters – accumulator ────────────────────────────────────────────────

  onItemsAccumulatorFilterChange = (event: any, field: string) => {
    const value = event.target ? event.target.value : event;
    this.setState(s => ({ itemsAccumulatorFilters: { ...s.itemsAccumulatorFilters, [field]: value } }));
  };
  onItemsAccumulatorFilterClick = () =>
    this.setState(s => ({ isShowItemsAccumulatorFilter: !s.isShowItemsAccumulatorFilter }));
  itemsAccumulatorOnApplyFilterClick = () => this.setState({ isItemsAccumulatorFiltered: true });
  itemsAccumulatorOnClearFilterClick = () => {
    this.setState({
      itemsAccumulatorFilters: getTwoPanelEditorFilters(ITEMS_ACCUMULATOR_COLUMNS),
      isItemsAccumulatorFiltered: false
    });
  };

  // ─── Sort stubs ───────────────────────────────────────────────────────────

  onSortItemsClick = () => {};
  onSortItemsAccumulatorClick = () => {};

  // ─── Clash modal ──────────────────────────────────────────────────────────

  onClashClick = (player: any) => {
    const { user } = this.props;
    const { clashes, firstName, lastName } = player;
    const { fullClashEvents = [], intervalClashEvents = [] } = clashes || {};
    const fullIds = fullClashEvents.map((e: any) => e._id);
    const intervalIds = intervalClashEvents.map((e: any) => e._id);
    const allIds = Lazy([...fullIds, ...intervalIds])
      .uniq()
      .toArray();
    this.setState({ isLoading: true });
    const eventsPromises: Promise<SchoolEvent>[] = allIds.map((id: string) => getSchoolEvent(user, id));
    BPromise.all(eventsPromises).then((events: SchoolEvent[]) => {
      const names = events
        .map((ev, i) => `${i + 1}. ${getClashEventDisplayTitle(ev, user)} ${getEventClash(ev, fullIds, intervalIds)}`)
        .join('\n ');
      this.setState({
        isLoading: false,
        playerEventsModalText: `${firstName} ${lastName} is participating in:\n${names}`,
        isPlayerEventsModalOpen: true
      });
    });
  };

  onClosePlayerEventsModalClick = () => this.setState({ isPlayerEventsModalOpen: false });

  // ─── Club events modal ────────────────────────────────────────────────────

  onEditModeClick = (editMode: string) => this.setState({ editMode });
  onCloseClubEventsModal = () => this.setState({ isClubEventsModalOpen: false });

  onClubEventsSaveClick = () => {
    if (this.isSaveInProgress) return;

    const { nextTeam, editMode, event, itemsAccumulator } = this.state;
    const { user, history } = this.props;
    const eventId = this.getEventId();
    const teamId = this.getTeamId();
    const players = itemsAccumulator.map(p => ({ userId: p.id, permissionId: p.permissionId }));
    const team = { ...nextTeam, cloneOf: nextTeam.id, teamType: TEAM_TYPE.CLONE, players };
    this.isSaveInProgress = true;
    this.setState({ isLoading: true });
    const savePromise = editMode === CLUB_EVENT_EDIT_MODE.SINGLE
      ? removeSchoolEventTeam(user, eventId, teamId)
        .then(() => addSchoolEventTeam(user, eventId, team))
      : addTeamInClubEvents(user, event.clubId, eventId, teamId, team);

    savePromise
      .then(() => history.push({ pathname: '/events/event', search: `id=${eventId}` }))
      .catch(() => {
        this.isSaveInProgress = false;
        this.setState({ isLoading: false, isError: true });
      });
  };

  // ─── Save / Cancel ────────────────────────────────────────────────────────

  onSaveClick = () => {
    const { nextTeam, event, itemsAccumulator } = this.state;
    const { user, history } = this.props;
    const eventId = this.getEventId();
    const teamId = this.getTeamId();
    const isReplaceMode = Boolean(teamId);

    if (!nextTeam) {
      this.setState({ isError: true });
      return;
    }

    if (this.isSaveInProgress) return;

    if (isClubEvent(event)) {
      this.setState({ isClubEventsModalOpen: true });
      return;
    }

    const players = itemsAccumulator.map(p => ({ userId: p.id, permissionId: p.permissionId }));
    const team = { ...nextTeam, cloneOf: nextTeam.id, teamType: TEAM_TYPE.CLONE, players };

    this.isSaveInProgress = true;
    this.setState({ isLoading: true });
    const saveTeamPromise = isReplaceMode
      ? removeSchoolEventTeam(user, eventId, teamId).then(() => addSchoolEventTeam(user, eventId, team))
      : addSchoolEventTeam(user, eventId, team);

    saveTeamPromise
      .then(() => history.push({ pathname: '/events/event', search: `id=${eventId}` }))
      .catch(() => {
        this.isSaveInProgress = false;
        this.setState({ isLoading: false, isError: true });
      });
  };

  openNewTeamMode = () => {
    const eventId = this.getEventId();
    const houseId = this.getHouseId();
    const params = new URLSearchParams({ event: eventId });
    if (houseId) params.set('house', houseId);
    const teamId = this.getTeamId();
    if (this.state.event && isClubEvent(this.state.event) && teamId) params.set('team', teamId);
    this.props.history.push({ pathname: '/events/teamManager', search: params.toString() });
  };

  onCancelClick = () => {
    const eventId = this.getEventId();
    this.props.history.push({ pathname: '/events/event', search: `id=${eventId}` });
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  renderDsIcon = (path: string, size = 20): React.ReactNode => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d={path} />
    </svg>
  );

  render() {
    const {
      isLoading,
      isError,
      event,
      forms,
      houses,
      nextTeam,
      items,
      itemsCount,
      itemsSelected,
      itemsFilters,
      isShowItemsFilter,
      isItemsFiltered,
      isSelectAllItemsChecked,
      sortItemsDirection,
      sortItemsColumnsName,
      itemCurrentPage,
      itemsAccumulator,
      itemsAccumulatorCount,
      itemsAccumulatorSelected,
      itemsAccumulatorFilters,
      isShowItemsAccumulatorFilter,
      isItemsAccumulatorFiltered,
      isSelectAllItemsAccumulatorChecked,
      sortItemsAccumulatorDirection,
      sortItemsAccumulatorColumnsName,
      itemsAddBuffer,
      itemsRemoveBuffer,
      isPlayerEventsModalOpen,
      isClubEventsModalOpen,
      itemsHiddenFields,
      itemsAccumulatorHiddenFields
    } = this.state;

    if (isLoading) return <Loader />;
    if (!event) return null;

    const { user } = this.props;
    const { activeSchool } = user;
    const { ageGroupsNaming, isShowStudentEventClashes } = activeSchool;

    const allItemsColumns = isShowStudentEventClashes ? ITEMS_COLUMNS_WITH_CLASHES : ITEMS_COLUMNS;
    const allAccumulatorColumns = isShowStudentEventClashes
      ? ITEMS_ACCUMULATOR_COLUMNS_WITH_CLASHES
      : ITEMS_ACCUMULATOR_COLUMNS;
    const visibleItemsColumns = allItemsColumns.filter(col => !itemsHiddenFields.has(col.field));
    const visibleAccumulatorColumns = allAccumulatorColumns.filter(col => !itemsAccumulatorHiddenFields.has(col.field));

    const itemsFiltersOptions = {
      ages: getSelectOptionForAge(forms, ageGroupsNaming),
      gender: getSelectOptionForGender(),
      forms: getSelectOptionForForms(forms),
      houses: getSelectOptionForHouses(houses)
    };
    const teamGenderPreset = nextTeam ? getUserGenderArrayConvertedFromEventGender(nextTeam.gender) : [];
    const itemsPresetFilters = {
      ages: nextTeam ? (nextTeam.ages || []).map(age => String(age)) : [],
      gender: teamGenderPreset.length === 1 ? teamGenderPreset.map(String) : []
    };

    const classes = isPlayerEventsModalOpen || isClubEventsModalOpen ? 'mt-3 modal-open' : 'mt-3';
    const isReplaceMode = Boolean(this.getTeamId());

    return (
      <div className={`${classes} eSchoolEventAddExistingTeam`}>
        <ClashEventsModal
          isOpen={isPlayerEventsModalOpen}
          text={this.state.playerEventsModalText}
          onClose={this.onClosePlayerEventsModalClick}
        />
        {isClubEventsModalOpen && (
          <div className="eClubScopeModal" role="presentation" onMouseDown={this.onCloseClubEventsModal}>
            <section
              className="eClubScopeModal__dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="club-team-scope-title"
              onMouseDown={event => event.stopPropagation()}
            >
              <header>
                <span className="eClubScopeModal__icon" aria-hidden="true">
                  {this.renderDsIcon('M7 7h10v10H7zM4 4h10M4 4v10M10 20h10V10', 20)}
                </span>
                <div>
                  <h2 id="club-team-scope-title">Apply team change</h2>
                  <p>Choose how far this replacement should apply in the club series.</p>
                </div>
                <button type="button" aria-label="Close" onClick={this.onCloseClubEventsModal}>×</button>
              </header>
              <SchoolEventClubEventRadioButtons onEditModeClick={this.onEditModeClick} editMode={this.state.editMode} />
              <footer>
                <button type="button" onClick={this.onCloseClubEventsModal}>Cancel</button>
                <button type="button" className="is-primary" onClick={this.onClubEventsSaveClick}>Apply change</button>
              </footer>
            </section>
          </div>
        )}

        <section className="eSchoolEventAddExistingTeam__buildCard">
          <div className="eSchoolEventAddExistingTeam__buildHead">
            <span className="eSchoolEventAddExistingTeam__buildIcon">
              {this.renderDsIcon('M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', 18)}
            </span>
            <div className="eSchoolEventAddExistingTeam__headCopy">
              <h3 className="eSchoolEventAddExistingTeam__title">
                {isReplaceMode ? 'Choose a replacement team' : 'Add a team'}
              </h3>
              <div className="eSchoolEventAddExistingTeam__subtitle">
                {isReplaceMode
                  ? 'Select a saved school team, then review and adjust its players before replacing the current squad.'
                  : 'Select a saved school team, then review and adjust its players before adding it to this event.'}
              </div>
            </div>
            {((isClubEvent(event) && isTeamSportEvent(event)) || (!isClubEvent(event) && !isReplaceMode)) && (
              <div
                className="bEventParticipantsMode"
                role="tablist"
                aria-label={isClubEvent(event) ? 'Participant editing mode' : 'Add team mode'}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected="false"
                  className="bEventParticipantsMode__btn"
                  onClick={this.openNewTeamMode}
                >
                  {isClubEvent(event) ? 'Edit participants' : 'Add new team'}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected="true"
                  className="bEventParticipantsMode__btn bEventParticipantsMode__btn--active"
                >
                  {isClubEvent(event) ? 'Choose existing team' : 'Add existing team'}
                </button>
              </div>
            )}
          </div>
          <div className="eSchoolEventAddExistingTeam__divider" />
          <div className="eSchoolEventAddExistingTeam__buildBody">
            <div className="eSchoolEventAddExistingTeam__teamPicker">
              <label className="eSchoolEventAddExistingTeam__label">
                Existing team <span className="eSchoolEventAddExistingTeam__required">*</span>
              </label>
              <Autocomplete
                searchFunction={this.getSchoolTeams}
                getElementTitle={getNameAndAges}
                onSelect={this.onSelectTeam}
                defaultItem={nextTeam}
                placeholder={'Search teams by name'}
                customClass="eSchoolEventAddExistingTeam__autocomplete"
              />
              <div className="eSchoolEventAddExistingTeam__hint">
                Results are matched to the event's sport, age group and gender.
              </div>
              {isError && <div className="eSchoolEventAddExistingTeam__fieldError">Please select a team.</div>}
            </div>

            {nextTeam && (
              <div className="eSchoolEventAddExistingTeam__selectedTeam">
                <div className="eSchoolEventAddExistingTeam__selectedHead">
                  <div>
                    <div className="eSchoolEventAddExistingTeam__selectedLabel">
                      {isReplaceMode ? 'Replacement team' : 'Team to add'}
                    </div>
                    <div className="eSchoolEventAddExistingTeam__selectedName">{getNameAndAges(nextTeam)}</div>
                  </div>
                  <div className="eSchoolEventAddExistingTeam__selectedCount">
                    {itemsAccumulatorCount} player{itemsAccumulatorCount === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="eSchoolEventAddExistingTeam__editorWrap">
                  <TwoPanelEditor
                    isSourceFirst
                    itemsAccumulator={itemsAccumulator}
                    itemsAccumulatorTitle={'Selected students'}
                    itemsAccumulatorColumns={visibleAccumulatorColumns}
                    onItemAccumulatorClick={this.onItemAccumulatorClick}
                    itemsAccumulatorSelected={itemsAccumulatorSelected}
                    onItemsAccumulatorFilterChange={this.onItemsAccumulatorFilterChange}
                    itemsAccumulatorFilters={itemsAccumulatorFilters}
                    itemsAccumulatorFiltersOptions={{}}
                    isShowItemsAccumulatorFilter={isShowItemsAccumulatorFilter}
                    isItemsAccumulatorFiltered={isItemsAccumulatorFiltered}
                    onItemsAccumulatorFilterClick={this.onItemsAccumulatorFilterClick}
                    itemsAccumulatorCount={itemsAccumulatorCount}
                    itemAccumulatorCurrentPage={1}
                    onSelectAllItemsAccumulatorClick={this.onSelectAllItemsAccumulatorClick}
                    onSelectAllItemsAccumulatorOnPageClick={this.onSelectAllItemsAccumulatorOnPageClick}
                    onUnselectAllItemsAccumulatorClick={this.onUnselectAllItemsAccumulatorClick}
                    isSelectAllItemsAccumulatorChecked={isSelectAllItemsAccumulatorChecked}
                    itemsAccumulatorOnApplyFilterClick={this.itemsAccumulatorOnApplyFilterClick}
                    itemsAccumulatorOnClearFilterClick={this.itemsAccumulatorOnClearFilterClick}
                    sortItemsAccumulatorDirection={sortItemsAccumulatorDirection}
                    sortItemsAccumulatorColumnsName={sortItemsAccumulatorColumnsName}
                    onSortItemsAccumulatorClick={this.onSortItemsAccumulatorClick}
                    items={items}
                    itemsTitle={'Available students'}
                    itemsColumns={visibleItemsColumns}
                    itemsSelected={itemsSelected}
                    onItemClick={this.onItemClick}
                    onItemsFilterChange={this.onItemsFilterChange}
                    itemsFilters={itemsFilters}
                    itemsFiltersOptions={itemsFiltersOptions}
                    itemsPresetFilters={itemsPresetFilters}
                    onReplaceItemsFilters={this.onReplaceItemsFilters}
                    onItemsSearchChange={this.onItemsSearchChange}
                    itemsSearchValue={this.state.itemsSearch}
                    isItemsLoading={this.state.isItemsLoading}
                    isShowItemsFilter={isShowItemsFilter}
                    isItemsFiltered={isItemsFiltered}
                    onItemsFilterClick={this.onItemsFilterClick}
                    itemsCount={itemsCount}
                    itemCurrentPage={itemCurrentPage}
                    itemsSetCurrentPageParams={this.itemsSetCurrentPageParams}
                    onSelectAllItemsClick={this.onSelectAllItemsClick}
                    onSelectAllItemsOnPageClick={this.onSelectAllItemsOnPageClick}
                    onUnselectAllItemsClick={this.onUnselectAllItemsClick}
                    isSelectAllItemsChecked={isSelectAllItemsChecked}
                    itemsOnApplyFilterClick={this.itemsOnApplyFilterClick}
                    itemsOnClearFilterClick={this.itemsOnClearFilterClick}
                    sortItemsDirection={sortItemsDirection}
                    sortItemsColumnsName={sortItemsColumnsName}
                    onSortItemsClick={this.onSortItemsClick}
                    onAddClick={this.onAddClick}
                    onRemoveClick={this.onRemoveClick}
                    itemsAddBuffer={itemsAddBuffer}
                    itemsRemoveBuffer={itemsRemoveBuffer}
                    onCellItemsClick={this.onClashClick}
                    onCellItemsAccumulatorClick={this.onClashClick}
                    itemsAllColumns={allItemsColumns}
                    itemsHiddenFields={itemsHiddenFields}
                    onToggleItemsColumn={this.onToggleItemsColumn}
                    isItemsColumnToggleable={this.isColumnToggleable}
                    itemsAccumulatorAllColumns={allAccumulatorColumns}
                    itemsAccumulatorHiddenFields={itemsAccumulatorHiddenFields}
                    onToggleItemsAccumulatorColumn={this.onToggleItemsAccumulatorColumn}
                    isItemsAccumulatorColumnToggleable={this.isColumnToggleable}
                  />
                </div>
              </div>
            )}
          </div>
        </section>

        <div className="eSchoolEventAddExistingTeam__actions">
          <button type="button" className="eSchoolEventAddExistingTeam__btnBack" onClick={this.onCancelClick}>
            {this.renderDsIcon('M19 12H5M11 18l-6-6 6-6', 15)}
            Cancel
          </button>
          <span className="eSchoolEventAddExistingTeam__actionsSpacer" />
          {nextTeam && (
            <span className="eSchoolEventAddExistingTeam__teamsCount">
              {itemsAccumulatorCount} player{itemsAccumulatorCount === 1 ? '' : 's'} selected
            </span>
          )}
          <button
            type="button"
            className="eSchoolEventAddExistingTeam__btnCreate"
            disabled={!nextTeam}
            onClick={this.onSaveClick}
          >
            {this.renderDsIcon('M20 6 9 17l-5-5', 15)}
            {isReplaceMode ? 'Replace team' : 'Add team'}
          </button>
        </div>
      </div>
    );
  }
}
